import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X, Bot, User } from "lucide-react";
import { FgLogo } from "@/components/FgLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "Che auto avete a Napoli Centrale?",
  "Come funziona la consegna da 5 a 90 minuti?",
  "Serve la carta di credito?",
  "Dove vi trovate a Napoli?",
];

const Bubble = ({ role, text }) => (
  <div className={`flex gap-2 ${role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-bubble-${role}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
        role === "user" ? "bg-fr-navy text-white rounded-br-md" : "bg-gray-100 text-gray-800 rounded-bl-md"
      }`}
    >
      {text}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    sessionRef.current = localStorage.getItem("fr_chat_session") || (() => {
      const id = crypto.randomUUID();
      localStorage.setItem("fr_chat_session", id);
      return id;
    })();
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API}/assistant/history/${sessionRef.current}`)
        .then((r) => r.json())
        .then((d) => d.messages && setMessages(d.messages))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: data.error };
              return c;
            });
          } else if (data.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.delta };
              return c;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Connessione interrotta. Riprova o chiama 350 965 6394." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed right-4 bottom-[84px] md:bottom-6 md:right-6 z-50 h-14 w-14 rounded-full bg-fr-navy text-white shadow-lift flex items-center justify-center"
        data-testid="chat-toggle-button"
        aria-label={open ? "Chiudi assistente" : "Apri assistente AI"}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span key={open ? "x" : "chat"} initial={{ rotate: -60, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 60, opacity: 0 }} transition={{ duration: 0.15 }}>
            {open ? <X size={22} /> : <MessageCircle size={22} />}
          </motion.span>
        </AnimatePresence>
        {!open && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-fr-red border-2 border-white" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", bounce: 0.2, duration: 0.45 }}
            className="fixed right-3 bottom-[152px] md:right-6 md:bottom-24 z-50 w-[calc(100vw-24px)] md:w-[380px] rounded-2xl bg-white shadow-lift border border-gray-100 overflow-hidden flex flex-col"
            style={{ maxHeight: "min(560px, calc(100vh - 200px))" }}
            data-testid="chat-panel"
          >
            <div className="bg-fr-navy px-4 py-3 flex items-center gap-3">
              <FgLogo size={32} />
              <div className="flex-1 leading-tight">
                <p className="text-white font-bold text-sm">Assistente FabriRent</p>
                <p className="text-white/60 text-[10px] flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 inline-block" /> Claude AI • online
                </p>
              </div>
              <Bot size={18} className="text-white/50" />
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 min-h-[180px]" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-400">Ciao! Sono l'assistente di FabriRent Napoli.</p>
                  <p className="text-[10px] text-gray-400 mt-1">Chiedimi di flotta, consegne, stazioni o prenotazioni.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.content || "…"} />
              ))}
              {streaming && messages[messages.length - 1]?.content === "" && (
                <div className="flex gap-1 px-2" data-testid="chat-typing">
                  {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-gray-400" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: i * 0.2 }} />
                  ))}
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="chat-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-600 hover:border-fr-navy hover:text-fr-navy transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-gray-100 p-2.5 flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3">
                <User size={14} className="text-gray-400 shrink-0" />
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Scrivi un messaggio…"
                  className="w-full bg-transparent h-10 text-[13px] outline-none"
                  data-testid="chat-input"
                />
              </div>
              <button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="h-10 w-10 rounded-xl bg-fr-navy text-white flex items-center justify-center disabled:opacity-40 hover:bg-fr-navydark transition-colors shrink-0"
                data-testid="chat-send-button"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
